<template>
  <ManageableSelectField
    v-model="model"
    :input-id="inputId"
    :label="label"
    :options="countryOptions"
    option-label="label"
    option-value="value"
    :disabled="disabled"
    :invalid="invalid"
    :hint="hint"
    :can-add="canAdd"
    :can-delete="canDelete"
    :delete-disabled="countryDeleteDisabled"
    :delete-disabled-message="countryDeleteDisabledMessage"
    :show-clear="showClear"
    add-label="Add Country"
    create-placeholder="New country name"
    delete-confirm-header="Delete country"
    :delete-confirm-message="deleteConfirmMessage"
    :max-length="80"
    :create-option="createCountry"
    :delete-option="deleteCountry"
  />
</template>

<script setup>
import { computed, watch } from 'vue';
import ManageableSelectField from '@/components/forms/ManageableSelectField.vue';
import {
  extraCountriesRef,
  rememberExtraCountry,
  restoreSuppressedCountry,
  suppressCountry,
  suppressedCountriesRef,
} from '@/utils/countryCatalogState';
import {
  buildCountryOptions,
  countLocationsForCountry,
  countriesMatch,
  countryOptionLabel,
  normalizeCountry,
} from '@/utils/countryCatalog';

const props = defineProps({
  label: { type: String, default: 'Country' },
  locations: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  hint: { type: String, default: '' },
  inputId: { type: String, default: '' },
  canAdd: { type: Boolean, default: true },
  canDelete: { type: Boolean, default: false },
  showClear: { type: Boolean, default: false },
  includeCanonical: { type: Boolean, default: true },
});

const model = defineModel({ default: '' });

watch(
  () => model.value,
  (value) => {
    const next = normalizeCountry(value);
    if (value == null && next === '') {
      model.value = '';
      return;
    }
    if (next !== value) model.value = next;
  },
  { immediate: true }
);

const countryOptions = computed(() =>
  buildCountryOptions(props.locations, [model.value, ...extraCountriesRef.value], {
    includeCanonical: props.includeCanonical,
    exclude: suppressedCountriesRef.value,
  })
);

const usedLocationCount = computed(() => countLocationsForCountry(props.locations, model.value));
const countryDeleteDisabled = computed(() => usedLocationCount.value > 0);
const countryDeleteDisabledMessage = computed(() => {
  const used = usedLocationCount.value;
  if (!used) return '';
  return used === 1
    ? 'Cannot delete: 1 work site uses this country. Delete that location first.'
    : `Cannot delete: ${used} work sites use this country. Delete those locations first.`;
});

const deleteConfirmMessage = computed(() => {
  const label = countryOptionLabel(model.value) || model.value || 'this country';
  return `Delete "${label}" from the country list? Work sites in this country must be deleted first.`;
});

function createCountry(name) {
  const value = normalizeCountry(name);
  if (!value) {
    throw new Error('Country is required');
  }
  restoreSuppressedCountry(value);
  const existing = countryOptions.value.find((opt) => countriesMatch(opt.value, value));
  if (existing) {
    return existing;
  }
  rememberExtraCountry(value);
  return { label: countryOptionLabel(value), value };
}

function deleteCountry(value) {
  const canonical = normalizeCountry(value);
  const used = countLocationsForCountry(props.locations, canonical);
  if (used > 0) {
    throw new Error(
      used === 1
        ? 'Cannot delete: 1 work site uses this country. Delete that location first.'
        : `Cannot delete: ${used} work sites use this country. Delete those locations first.`
    );
  }
  suppressCountry(canonical);
  if (countriesMatch(model.value, canonical)) {
    model.value = '';
  }
}
</script>
